// ERFAccessoriesModal.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import ItemService from "../../../../services/customer_appCommonService";
import { SearchField } from "../../../../Components/formElements/SearchField";

const ERFAccessoriesModal = React.forwardRef((props, ref) => {
  // ---------------- STATE ----------------
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  // eslint-disable-next-line
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTxt, setSearchTxt] = useState("");
  // eslint-disable-next-line
  const [isSearching, setIsSearching] = useState(false);

  // CART LOGIC
  const [selectedItems, setSelectedItems] = useState(new Map());
  const [isCardVisible, setCardVisible] = useState(true);

  // ---------------- HELPER ----------------
  // We keep the barcode as the internal unique key so the system knows exactly
  // which physical items are selected, even though it's hidden from the UI.
  const getRowKey = useCallback((row) => row.barcode, []);

  // ---------------- API ----------------
  const fetchAccessories = async (page, size, search) => {
    const codes =
      props.parentItem?.accessoriesCode?.map((acc) => acc.materialCode) || [];

    if (codes.length === 0) {
      toast.success("No accessories configured for this item.");
      setData([]);
      setTotalRows(0);
      return;
    }

    setLoading(true);
    if (search) setIsSearching(true);

    try {
      const godownCode = props.godownCode || "RE";

      const payload = {
        queryName: "EquipmentNotAssignedBarcodeList",
        godownCode: godownCode,
        page: page,
        per_page: size,
        searchTerm: search.trim(),
        timeZone: "Asia/Calcutta",
        rowFilter: {
          materialCode: { $in: codes },
        },
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200) {
        setData(response.data || []);
        setTotalRows(response.total || 0);

        if (search && (!response.data || response.data.length === 0)) {
          toast.success(`No accessories found matching "${search}"`);
        }
      } else {
        setData([]);
        setTotalRows(0);
        toast.error(response?.message || "Failed to fetch accessories");
      }
    } catch (err) {
      console.error("Accessories fetch error:", err);
      setData([]);
      setTotalRows(0);
      toast.error("Error loading accessories. Please try again.");
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // ---------------- SEARCH ----------------
  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setCurrentPage(1);
        fetchAccessories(1, perPage, val);
      }, 400),
    // eslint-disable-next-line
    [perPage, props.parentItem],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTxt(val);

    if (!val.trim()) {
      debouncedSearch.cancel();
      setCurrentPage(1);
      fetchAccessories(1, perPage, "");
    } else {
      debouncedSearch(val);
    }
  };

  const handleClearSearch = () => {
    setSearchTxt("");
    setCurrentPage(1);
    debouncedSearch.cancel();
    fetchAccessories(1, perPage, "");
  };

  // ---------------- PAGINATION ----------------
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAccessories(page, perPage, searchTxt);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchAccessories(page, newPerPage, searchTxt);
  };

  // ---------------- INIT ----------------
  useEffect(() => {
    if (props.show && props.parentItem) {
      setData([]); // ← clear immediately
      setTotalRows(0);
      setSelectedItems(new Map());
      setSearchTxt("");
      setCurrentPage(1);
      setIsSearching(false);
      fetchAccessories(1, perPage, "");
    } else {
      debouncedSearch.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, props.parentItem]);

  // ---------------- CART / SELECTION ----------------
  const handleSelectRow = useCallback(
    (row) => {
      const key = getRowKey(row);
      if (!key) return;
      setSelectedItems((prev) => {
        const map = new Map(prev);
        if (map.has(key)) {
          map.delete(key);
        } else {
          map.set(key, row);
        }
        return map;
      });
    },
    [getRowKey],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedItems((prev) => {
      const map = new Map(prev);
      const validRows = data.filter((r) => !!getRowKey(r));
      const allSelected =
        validRows.length > 0 && validRows.every((r) => map.has(getRowKey(r)));

      validRows.forEach((r) => {
        const key = getRowKey(r);
        if (allSelected) {
          map.delete(key);
        } else {
          map.set(key, r);
        }
      });
      return map;
    });
  }, [data, getRowKey]);

  const handleClearAll = () => {
    setSelectedItems(new Map());
  };

  const isRowSelected = useCallback(
    (row) => {
      const key = getRowKey(row);
      if (!key) return false;
      return selectedItems.has(key);
    },
    [selectedItems, getRowKey],
  );

  const areAllPageItemsSelected = useMemo(() => {
    if (data.length === 0) return false;
    const validRows = data.filter((r) => !!getRowKey(r));
    if (validRows.length === 0) return false;
    return validRows.every((r) => selectedItems.has(getRowKey(r)));
  }, [data, selectedItems, getRowKey]);

  const handleSubmit = () => {
    if (selectedItems.size === 0) {
      toast.error("Please select at least one accessory.");
      return;
    }
    // Capture parentItem before onHide clears it
    const parentItem = props.parentItem;
    props.onSave(Array.from(selectedItems.values()), parentItem);
    props.onHide();
  };

  // ---------------- TABLE COLUMNS ----------------
  const columns = useMemo(
    () => [
      {
        name: (
          <input
            type="checkbox"
            checked={areAllPageItemsSelected}
            onChange={handleSelectAll}
            disabled={loading || data.length === 0}
          />
        ),
        cell: (row) => (
          <input
            type="checkbox"
            checked={isRowSelected(row)}
            onChange={() => handleSelectRow(row)}
          />
        ),
        width: "60px",
        style: { justifyContent: "center" },
      },
      // The Barcode column has been removed here to hide it from the user
      {
        name: "Material Details",
        cell: (row) => (
          <div>
            <p className="m-0 fw-bold text-primary">
              {row.materialCode || "--"}
            </p>
            <p className="m-0 text-wrap">{row.materialDescription || "--"} </p>
          </div>
        ),
        width: "300px",
        sortable: true,
      },
      {
        name: "Material Group",
        cell: (row) => (
          <span className="text-muted">{row.materialGroup || "--"}</span>
        ),
        width: "160px",
        sortable: true,
      },
      {
        name: "Brand",
        cell: (row) => (
          <span className="fw-bold text-primary">{row.brand || "--"}</span>
        ),
        width: "120px",
        sortable: true,
      },
    ],
    [
      areAllPageItemsSelected,
      handleSelectAll,
      handleSelectRow,
      isRowSelected,
      loading,
      data.length,
    ],
  );

  // ---------------- UI ----------------
  return (
    <Modal
      {...props}
      dialogClassName="modal-90w"
      centered
      backdrop="static"
      keyboard={false}
      ref={ref}
      style={{ minHeight: "85vh" }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <span className="fw-bold">Select Accessories</span>
            {props.parentItem && (
              <div className="small text-muted mt-1">
                For:{" "}
                <span className="text-primary fw-bold">
                  {props.parentItem.materialCode}
                </span>
                {" — "}
                {props.parentItem.materialDescription}
              </div>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{ minHeight: "600px", maxHeight: "80vh", overflow: "auto" }}
      >
        {/* SEARCH BAR */}
        <Row className="mb-3">
          <Col
            xs={12}
            md={{ offset: 1, span: 4 }}
            lg={{ offset: 1, span: 4 }}
            className="commLeftRightPadding"
          >
            <div className="position-relative">
              <SearchField
                value={searchTxt}
                onChange={handleSearchChange}
                placeholder="Search accessories..."
              />
              {searchTxt && (
                <i
                  className="bi bi-x-circle-fill position-absolute"
                  style={{
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#dc3545",
                    fontSize: "1.2rem",
                    zIndex: 5,
                  }}
                  onClick={handleClearSearch}
                  title="Clear search"
                ></i>
              )}
            </div>
            {searchTxt && (
              <small className="text-muted d-block mt-1">
                Searching for "{searchTxt}"
              </small>
            )}
          </Col>
        </Row>

        {/* SELECTED ITEMS CART */}
        {selectedItems.size > 0 && (
          <Row className="mb-3">
            <Col xs={12}>
              <div className="border rounded p-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>
                    <i className="bi me-2"></i>
                    Selected Items ({selectedItems.size})
                  </strong>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleClearAll}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Clear All
                  </button>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {Array.from(selectedItems.values()).map((item) => {
                    const key = getRowKey(item);
                    return (
                      <span
                        key={key}
                        className="badge bg-info d-flex align-items-center gap-2"
                        style={{
                          fontSize: "0.9rem",
                          padding: "0.5rem 0.75rem",
                        }}
                      >
                        <span>{item.materialCode}</span>
                        <i
                          className="bi bi-x-circle-fill"
                          style={{ cursor: "pointer", fontSize: "1rem" }}
                          onClick={() => handleSelectRow(item)}
                          title="Remove"
                        ></i>
                      </span>
                    );
                  })}
                </div>
              </div>
            </Col>
          </Row>
        )}

        {/* MATERIAL TABLE */}
        <Row>
          <Col xs={12}>
            <div className="accordion">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button align-items-center">
                    <Col xs={1} className="text-center">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-right" : "bi-chevron-down"
                        }`}
                        onClick={() => setCardVisible(!isCardVisible)}
                      ></i>
                    </Col>
                    <Col xs={11}>
                      <span className="headerTitle">Accessory List</span>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <div
                      className="position-relative"
                      style={{
                        height: "450px",
                        overflow: "hidden",
                      }}
                    >
                      {/* DataTable */}
                      <DataTable
                        columns={columns}
                        data={data}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        fixedHeader
                        fixedHeaderScrollHeight="380px"
                        noDataComponent={
                          <div className="text-center py-5">
                            <i className="bi bi-inbox text-muted"></i>
                            <p className="text-muted mt-3 mb-2">
                              {searchTxt
                                ? `No accessories found matching "${searchTxt}"`
                                : "No stock available for the required accessories."}
                            </p>
                            {searchTxt && (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={handleClearSearch}
                              >
                                <i className="bi bi-x-circle me-1"></i> Clear
                                Search
                              </button>
                            )}
                          </div>
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      {/* FOOTER */}
      <Modal.Footer className="bg-light">
        <Row className="w-100 align-items-center">
          <Col xs={6} className="text-start">
            {selectedItems.size > 0 && (
              <span className="text-muted fw-medium">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                {selectedItems.size} item(s) selected
              </span>
            )}
          </Col>
          <Col xs={6} className="text-end">
            <button
              type="button"
              className="btn btn-danger mt-4 me-4 justify-center rounded-md"
              onClick={props.onHide}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary mt-4 justify-center rounded-md"
              disabled={selectedItems.size === 0}
              onClick={handleSubmit}
            >
              Select
            </button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
});

ERFAccessoriesModal.displayName = "ERFAccessoriesModal";
export default ERFAccessoriesModal;
